import "./css";
import "./css/PatientAudioNote.css";
import "./css/SmartPrescriptionModal.css";
import "./css/tailwind.css";
// Loaded globally so the body marker can also scope portaled content.
import "./css/redesign-accent-recolor.css";

import * as Sentry from "@sentry/react";
import React, { StrictMode, useEffect } from "react";
import { isMobile } from "react-device-detect";
import { createRoot } from "react-dom/client";
// The package's React 17-era types reject this provider under React 18; cast it at the import boundary.
import { Provider as IntlReduxProvider } from "react-intl-redux";
import { Route, type RouteComponentProps, type RouteProps, BrowserRouter as Router, Switch } from "react-router-dom";

import { store } from "./app-store";
import { featureFlags } from "./common/feature-flags";
import { isUser, useOptionalUser } from "./common/user";
import { isDevelopment } from "./common/utils";
import PlanApprovePage from "./components/3d_plan_approve/3d_plan_approve_page";
import PlanCorrectionPage from "./components/3d_plan_correction/3d_plan_correction_page";
import PlanPowerCapPage from "./components/3d_plan_power_cap/3d_plan_power_cap_page";
import PlanRetainersPage from "./components/3d_plan_retainers/3d_plan_retainers_page";
import PlanReworkPage from "./components/3d_plan_rework/3d_plan_rework_page";
import AdditionalAlignersPage from "./components/additional_aligners/additional_aligners_page";
import AdditionalAlignersTablePage from "./components/additional_aligners/additional_aligners_table";
import CatchAll from "./components/catch_all";
import ClinicNewPage from "./components/clinic/clinic_new_page";
import ClinicPage from "./components/clinic/clinic_page";
import ClinicsListPage from "./components/clinic/clinics_list";
import Contacts from "./components/common/contacts";
import PaymentDetails from "./components/common/payment_details";
import DoctorChangePasswordPage from "./components/doctor/doctor_change_password_page";
import DoctorPage from "./components/doctor/doctor_page";
import DoctorUpdatePage from "./components/doctor/doctor_update_page";
import ShipmentsPage from "./components/doctor/shipments_page";
import ElitePage, { ElitePaymentStatus } from "./components/elite_page";
import ForgotPassword from "./components/forgot_password";
import HomePage from "./components/homepage";
import Login from "./components/login";
import { MaintenanceBanner } from "./components/maintenance-banner/maintenance-banner";
import ManualsPage from "./components/manuals/manuals_page";
import Notification from "./components/notification";
import { notificationCustomStyle } from "./components/notifications/notification_styles";
import PatientDeliveryPage from "./components/patient/patient_delivery_page";
import PatientNewPage from "./components/patient/patient_new";
import PatientPage from "./components/patient/patient_page";
import PaymentInvoice from "./components/patient/patient_show/payment_invoice/payment_invoice";
import PaymentInvoiceBatchStatus from "./components/patient/patient_show/payment_invoice/payment_invoice_batch_status";
import PaymentInvoiceErrorBody from "./components/patient/patient_show/payment_invoice/payment_invoice_error_body";
import PaymentInvoiceStatus from "./components/patient/patient_show/payment_invoice/payment_invoice_status";
import PaymentInvoiceSuccessBody from "./components/patient/patient_show/payment_invoice/payment_invoice_success_body";
import PatientUpdatePage from "./components/patient/patient_update";
import PatientsListPage from "./components/patient/patients_list_new";
import PatientsPayPage from "./components/patient/patients_pay_list";
import PatientsTaskPage from "./components/patient/patients_tasks_list";
import PatientArea from "./components/patient_area";
import PatientPaymentError from "./components/patient_area/payment_error";
import PatientPaymentStatus from "./components/patient_area/payment_status";
import PatientPaymentSuccess from "./components/patient_area/payment_success";
import PromoMaterialsPage from "./components/promo_materials/promo_materials_page";
import { AuthOnly, PublicOnly, SignedOfferOnly } from "./components/public_only";
import PasswordRecovery from "./components/recovery";
import { RedesignForgotPasswordEntry, RedesignLoginEntry, RedesignRecoveryEntry } from "./components/redesign-v1/auth/auth-entry";
import ClinicFormEntry from "./components/redesign-v1/clinic/clinic-form-entry";
import DashboardRedesignEntry from "./components/redesign-v1/dashboard/dashboard-entry";
import DeliveryEntry from "./components/redesign-v1/delivery/delivery-entry";
import DocumentsRedesignEntry from "./components/redesign-v1/documents/documents-entry";
import EducationRedesignEntry from "./components/redesign-v1/education/education-entry";
// Event detail graduated from the legacy-wrap entry (`EventDetailRedesignEntry`, now OBSOLETE — kept as a
// one-line revert switch) to a REBUILT native page, pixel-matched to Figma frame 7342-31306.
import EventDetailEntry from "./components/redesign-v1/events/event-detail-entry";
import ExpertConsultationEntry from "./components/redesign-v1/expert-consultation/expert-consultation-entry";
import {
  ClinicsListRedesignEntry,
  ElitePaymentStatusRedesignEntry,
  EliteRedesignEntry,
  HomeRedesignEntry,
  PatientsTasksRedesignEntry,
  PaymentInvoiceErrorRedesignEntry,
  PaymentInvoiceRedesignEntry,
  PaymentInvoiceSuccessRedesignEntry,
} from "./components/redesign-v1/legacy-pages/adopted-routes-entry";
import {
  AdditionalAlignersOrderRedesignEntry,
  AdditionalAlignersTableRedesignEntry,
  ChatRedesignEntry,
  EventPaymentErrorRedesignEntry,
  EventPaymentStatusRedesignEntry,
  EventPaymentSuccessRedesignEntry,
  ManualsRedesignEntry,
  MeetingsFlowRedesignEntry,
  PatientDeliveriesRedesignEntry,
  PaymentInvoiceBatchStatusRedesignEntry,
  PaymentInvoiceStatusRedesignEntry,
  PlanCorrectionRedesignEntry,
  PlanPowerCapRedesignEntry,
  PlanRetainersRedesignEntry,
} from "./components/redesign-v1/legacy-pages/legacy-page-entry";
import ContactsRedesignEntry from "./components/redesign-v1/legal/contacts-entry";
import PaymentDetailsRedesignEntry from "./components/redesign-v1/legal/payment-details-entry";
import { EditPatientRedesignEntry, NewPatientRedesignEntry } from "./components/redesign-v1/patient-form/patient-form-entry";
import PatientViewRedesignEntry from "./components/redesign-v1/patient-view/patient-view-entry";
import PatientsRedesignEntry from "./components/redesign-v1/patients/patients-entry";
// The payments routes use the native redesign page rather than the legacy page inside a redesign shell.
import PaymentsRedesignEntry from "./components/redesign-v1/payments/payments-entry";
import { PlanApproveRedesignEntry, PlanReworkRedesignEntry } from "./components/redesign-v1/plan/plan-entry";
import ChangePasswordEntry from "./components/redesign-v1/profile/change-password-entry";
import ClinicViewEntry from "./components/redesign-v1/profile/clinic-view-entry";
import ClinicalPreferencesEntry from "./components/redesign-v1/profile/clinical-preferences-entry";
import ProfileEditEntry from "./components/redesign-v1/profile/profile-edit-entry";
import ProfileViewEntry from "./components/redesign-v1/profile/profile-view-entry";
// Promo materials use the native card-grid page instead of the legacy page inside a redesign shell.
import PromoRedesignEntry from "./components/redesign-v1/promo/promo-entry";
import { PwaOnboarding } from "./components/redesign-v1/pwa/pwa-onboarding";
import SignOffer from "./components/sign_offer/sign_offer";
import { SupportWidget } from "./components/support-widget";
import { Welcome } from "./components/welcome";
import YandexMetrikaPage from "./components/yandex-metrika";
import { AppErrorBoundary } from "./error-boundary";
import { loadDevTools } from "./features/dev-tools/load";
import { DoctorConsentGate } from "./features/doctor-consents/doctor-consent-gate";
import { PatientPlanPage } from "./features/doctor-viewer/patient-plan-page";
import DocumentsRoutes from "./features/documents/documents-routes";
import EventRoutes from "./features/events/events-routes";
import { MeetingFeedbackPopUp } from "./features/meetings/meeting-feedback-popup";
import MeetingsRoutes from "./features/meetings/meetings-routes";
import { AppToaster, SonnerToasterHost } from "./features/toasts/toaster";
import TreatmentReportEntry from "./features/treatment-report/route-entry";
import { requireOfferSign } from "./require-offer-sign";
import { initializeUser, selectUser } from "./slices/user";
import { useAppDispatch } from "./store";

const PatientPageRequireOfferSign = requireOfferSign(PatientPage);
const isPatientPlanPage = window.location.pathname.startsWith("/viewer/plans/");

const Provider = IntlReduxProvider as unknown as React.ComponentType<{ store: typeof store; children?: React.ReactNode }>;

/** Keep public payment information available without the authenticated redesign shell. */
function PaymentDetailsRouteEntry(props: RouteComponentProps) {
  const user = useOptionalUser();
  return user ? <PaymentDetailsRedesignEntry {...props} /> : <PaymentDetails />;
}

/** Keep public contacts available without the authenticated redesign shell. */
function ContactsRouteEntry(props: RouteComponentProps) {
  const user = useOptionalUser();
  return user ? <ContactsRedesignEntry {...props} /> : <Contacts />;
}

const REDESIGN_ROUTE_OVERRIDES: Array<{
  path: string;
  exact?: boolean;
  component: NonNullable<RouteProps["component"]>;
}> = [
  // The exact listing route must precede the adapter for meeting sub-routes.
  { path: "/pages/meetings", exact: true, component: requireOfferSign(ExpertConsultationEntry) },
  { path: "/pages/meetings", component: requireOfferSign(MeetingsFlowRedesignEntry) },
  { path: "/pages/patients", exact: true, component: requireOfferSign(PatientsRedesignEntry) },
  { path: "/pages/patient/:patient_id", exact: true, component: requireOfferSign(PatientViewRedesignEntry) },
  { path: "/pages/patient/:patient_id/deliveries", exact: true, component: requireOfferSign(PatientDeliveriesRedesignEntry) },
  { path: "/pages/patient/:patient_id/correct", exact: true, component: requireOfferSign(PlanCorrectionRedesignEntry) },
  { path: "/pages/patient/:patient_id/retainers", exact: true, component: requireOfferSign(PlanRetainersRedesignEntry) },
  { path: "/pages/patient/:patient_id/power-cap", exact: true, component: requireOfferSign(PlanPowerCapRedesignEntry) },
  { path: "/pages/patient/:patient_id/approve/:case_id", exact: true, component: requireOfferSign(PlanApproveRedesignEntry) },
  { path: "/pages/patient/:patient_id/rework/:case_id", exact: true, component: requireOfferSign(PlanReworkRedesignEntry) },
  { path: "/pages/patient/:patient_id/additional/aligners", exact: true, component: requireOfferSign(AdditionalAlignersOrderRedesignEntry) },
  { path: "/pages/patient/:patient_id/aligners/additional", exact: true, component: requireOfferSign(AdditionalAlignersTableRedesignEntry) },
  { path: "/pages/events", exact: true, component: requireOfferSign(EducationRedesignEntry) },
  // Payment callbacks must precede the generic event-detail route because `Switch` uses the first match.
  { path: "/pages/events/:event_id/payments/status/:transaction_id", component: requireOfferSign(EventPaymentStatusRedesignEntry) },
  { path: "/pages/events/:event_id/payments/success/:hash", component: requireOfferSign(EventPaymentSuccessRedesignEntry) },
  { path: "/pages/events/:event_id/payments/error", component: requireOfferSign(EventPaymentErrorRedesignEntry) },
  { path: "/pages/events/:event_id", component: requireOfferSign(EventDetailEntry) },
  { path: "/pages/achievements", exact: true, component: requireOfferSign(DashboardRedesignEntry) },
  { path: "/pages/new_patient", exact: true, component: requireOfferSign(NewPatientRedesignEntry) },
  { path: "/pages/edit_patient/:patient_id", exact: true, component: requireOfferSign(EditPatientRedesignEntry) },
  { path: "/pages/payments", exact: true, component: requireOfferSign(PaymentsRedesignEntry) },
  { path: "/pages/patient/:patient_id/payment/status/:invoice_id", component: requireOfferSign(PaymentInvoiceStatusRedesignEntry) },
  { path: "/pages/patient/payment/batch/status/:batch_id", component: requireOfferSign(PaymentInvoiceBatchStatusRedesignEntry) },
  { path: "/pages/shipments", exact: true, component: requireOfferSign(DeliveryEntry) },
  { path: "/pages/chat", exact: true, component: requireOfferSign(ChatRedesignEntry) },
  { path: "/pages/manuals", exact: true, component: requireOfferSign(ManualsRedesignEntry) },
  { path: "/pages/documents", component: requireOfferSign(DocumentsRedesignEntry) },
  { path: "/pages/promo", exact: true, component: requireOfferSign(PromoRedesignEntry) },
  { path: "/pages/my_account", exact: true, component: requireOfferSign(ProfileViewEntry) },
  { path: "/pages/edit_my_account", exact: true, component: requireOfferSign(ProfileEditEntry) },
  { path: "/pages/doctor/clinic/create", exact: true, component: requireOfferSign(ClinicFormEntry) },
  { path: "/pages/doctor/clinics/:clinic_id", exact: true, component: requireOfferSign(ClinicViewEntry) },
  { path: "/pages/my_account/preferences", exact: true, component: requireOfferSign(ClinicalPreferencesEntry) },
  { path: "/pages/account/password/update", exact: true, component: requireOfferSign(ChangePasswordEntry) },
  // Keep specific payment results before the prefix-matched payment route.
  { path: "/pages/patient/:patient_id/payment/success/:secure", component: requireOfferSign(PaymentInvoiceSuccessRedesignEntry) },
  { path: "/pages/patient/:patient_id/payment/success", component: requireOfferSign(PaymentInvoiceSuccessRedesignEntry) },
  { path: "/pages/patient/:patient_id/payment/error", component: requireOfferSign(PaymentInvoiceErrorRedesignEntry) },
  { path: "/pages/patient/payment/batch/success/:secure", component: requireOfferSign(PaymentInvoiceSuccessRedesignEntry) },
  { path: "/pages/patient/payment/batch/error/:secure", component: requireOfferSign(PaymentInvoiceErrorRedesignEntry) },
  { path: "/pages/patient/:patient_id/payment", component: requireOfferSign(PaymentInvoiceRedesignEntry) },
  { path: "/pages/tasks", component: requireOfferSign(PatientsTasksRedesignEntry) },
  { path: "/pages/doctor/clinics", component: requireOfferSign(ClinicsListRedesignEntry) },
  { path: "/pages/elite/payments/status/:transaction_id", component: ElitePaymentStatusRedesignEntry },
  { path: "/pages/elite", component: requireOfferSign(EliteRedesignEntry) },
  { path: "/pages/home", component: requireOfferSign(HomeRedesignEntry) },
  { path: "/payment/details", exact: true, component: PaymentDetailsRouteEntry },
  { path: "/contacts", exact: true, component: ContactsRouteEntry },
];

function App() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isPatientPlanPage) void dispatch(initializeUser());
  }, [dispatch]);

  // The body marker also scopes recoloring for portaled content.
  useEffect(() => {
    if (featureFlags.redesignV1) document.body.classList.add("rd-redesign");
  }, []);

  // Match the mobile browser chrome to the redesign header.
  useEffect(() => {
    if (!featureFlags.redesignV1) return;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = "#625a54";
  }, []);

  if (featureFlags.maintenanceMode) {
    return <MaintenanceBanner />;
  }

  return (
    <Router>
      <DoctorConsentGate
        afterConfirmed={
          <>
            <Notification style={notificationCustomStyle} />
            <SonnerToasterHost />
            <PwaOnboarding />
            <AuthOnly>
              <SupportWidget />
            </AuthOnly>

            <SignedOfferOnly>
              <AppToaster />
              <MeetingFeedbackPopUp />
            </SignedOfferOnly>
          </>
        }
      >
        <Route
          path="/pages"
          render={() => {
            const user = selectUser(store.getState());
            return isUser(user) ? null : <Welcome />;
          }}
        />
        <Switch>
          <Route exact path="/viewer/plans/:token" component={PatientPlanPage} />
          <Route exact path="/pages/patient/:patient_id/report" component={requireOfferSign(TreatmentReportEntry)} />
          {/* Override routes precede legacy routes because `Switch` uses the first match. */}
          {featureFlags.redesignV1 && featureFlags.redesignV1RouteOverride
            ? REDESIGN_ROUTE_OVERRIDES.map((r) => <Route key={`${r.path}:${r.exact ? "exact" : "nested"}`} exact={r.exact} path={r.path} component={r.component} />)
            : null}
          <Route path="/pages/offer" component={SignOffer} />
          {!isMobile && (window.location.hostname.endsWith(".ru") || isDevelopment) ? <Route path="/pages/home" component={requireOfferSign(HomePage)} /> : null}
          <Route path="/pages/elite/payments/status/:transaction_id" component={ElitePaymentStatus} />
          <Route path="/pages/elite" component={requireOfferSign(ElitePage)} />
          <Route path="/pages/patients" component={requireOfferSign(PatientsListPage)} />
          <Route path="/pages/patient/:patient_id/payment/success/:secure" component={requireOfferSign(PaymentInvoiceSuccessBody)} />
          <Route path="/pages/patient/:patient_id/payment/success" component={requireOfferSign(PaymentInvoiceSuccessBody)} />
          <Route path="/pages/patient/:patient_id/payment/status/:invoice_id" component={requireOfferSign(PaymentInvoiceStatus)} />
          <Route path="/pages/patient/payment/batch/status/:batch_id" component={requireOfferSign(PaymentInvoiceBatchStatus)} />
          <Route path="/pages/patient/payment/batch/success/:secure" component={requireOfferSign(PaymentInvoiceSuccessBody)} />
          <Route path="/pages/patient/payment/batch/error/:secure" component={requireOfferSign(PaymentInvoiceErrorBody)} />
          <Route path="/pages/patient/:patient_id/payment/error" component={requireOfferSign(PaymentInvoiceErrorBody)} />
          <Route path="/pages/patient/:patient_id/payment" component={requireOfferSign(PaymentInvoice)} />
          <Route path="/pages/patient/:patient_id/deliveries" component={requireOfferSign(PatientDeliveryPage)} />
          <Route path="/pages/patient/:patient_id/rework/:case_id" component={requireOfferSign(PlanReworkPage)} />
          <Route path="/pages/patient/:patient_id/correct" component={requireOfferSign(PlanCorrectionPage)} />
          <Route path="/pages/patient/:patient_id/retainers" component={requireOfferSign(PlanRetainersPage)} />
          <Route path="/pages/patient/:patient_id/power-cap" component={requireOfferSign(PlanPowerCapPage)} />
          <Route path="/pages/patient/:patient_id/additional/aligners" component={requireOfferSign(AdditionalAlignersPage)} />
          <Route path="/pages/patient/:patient_id/aligners/additional" component={requireOfferSign(AdditionalAlignersTablePage)} />
          <Route path="/pages/patient/:patient_id/approve/:case_id" component={requireOfferSign(PlanApprovePage)} />
          <Route path="/pages/my_account" component={requireOfferSign(DoctorPage)} />
          <Route path="/pages/edit_my_account" component={requireOfferSign(DoctorUpdatePage)} />
          <Route path="/pages/account/password/update" component={requireOfferSign(DoctorChangePasswordPage)} />
          <Route
            path="/pages/patient/:patient_id"
            render={(routeProps) => (
              // Remount the page when navigation changes the patient ID.
              <PatientPageRequireOfferSign key={routeProps.match.params.patient_id} {...routeProps} />
            )}
          />
          <Route path="/pages/edit_patient/:patient_id" component={requireOfferSign(PatientUpdatePage)} />
          <Route path="/pages/shipments" component={requireOfferSign(ShipmentsPage)} />
          <Route path="/pages/doctor/clinics/:clinic_id" component={requireOfferSign(ClinicPage)} />
          <Route path="/pages/doctor/clinics" component={requireOfferSign(ClinicsListPage)} />
          <Route path="/pages/doctor/clinic/create" component={requireOfferSign(ClinicNewPage)} />
          <Route path="/pages/tasks" component={requireOfferSign(PatientsTaskPage)} />
          <Route path="/pages/payments" component={requireOfferSign(PatientsPayPage)} />
          <Route path="/pages/manuals" component={requireOfferSign(ManualsPage)} />
          <Route path="/pages/promo" component={requireOfferSign(PromoMaterialsPage)} />
          <Route path="/pages/new_patient" component={requireOfferSign(PatientNewPage)} />
          {/* Keep redesign routes out of flag-off builds so their chunks are not loaded. */}
          {featureFlags.redesignV1 ? <Route path="/pages/redesign/meetings" component={requireOfferSign(ExpertConsultationEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/patients" component={requireOfferSign(PatientsRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? (
            <Route exact path="/pages/redesign/patient/:patient_id" render={(routeProps) => <PatientViewRedesignEntry key={routeProps.match.params.patient_id} {...routeProps} />} />
          ) : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/events" component={requireOfferSign(EducationRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? (
            <Route exact path="/pages/redesign/events/:event_id" render={(routeProps) => <EventDetailEntry key={routeProps.match.params.event_id} {...routeProps} />} />
          ) : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/new_patient" component={requireOfferSign(NewPatientRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/edit_patient/:patient_id" component={requireOfferSign(EditPatientRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/my_account" component={requireOfferSign(ProfileViewEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/edit_my_account" component={requireOfferSign(ProfileEditEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/shipments" component={requireOfferSign(DeliveryEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/manuals" component={requireOfferSign(ManualsRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route path="/pages/redesign/documents" component={requireOfferSign(DocumentsRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/payments" component={requireOfferSign(PaymentsRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/promo" component={requireOfferSign(PromoRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/doctor/clinic/create" component={requireOfferSign(ClinicFormEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/doctor/clinics/:clinic_id" component={requireOfferSign(ClinicViewEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/my_account/preferences" component={requireOfferSign(ClinicalPreferencesEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/patient/:patient_id/approve/:case_id" component={requireOfferSign(PlanApproveRedesignEntry)} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/patient/:patient_id/rework/:case_id" component={requireOfferSign(PlanReworkRedesignEntry)} /> : null}
          {/* Redesign auth entries handle signed-in redirects without the application shell. */}
          <Route
            path="/recover/password"
            render={() =>
              featureFlags.redesignV1 && featureFlags.redesignV1RouteOverride ? (
                <RedesignForgotPasswordEntry />
              ) : (
                <PublicOnly>
                  {/* No route props spread: ForgotPassword reads none, and the connected component's props
                      don't include RouteComponentProps. */}
                  <ForgotPassword />
                </PublicOnly>
              )
            }
          />
          {/*
                NOTE: <PublicOnly /> is not used with Login, because Login mutates user and userIsLoading state,
                which causes PublicOnly component to redirect the user to /pages/patients page.
                We want to determine where to redirect the user inside the Login component.
              */}
          <Route path="/login" render={() => (featureFlags.redesignV1 && featureFlags.redesignV1RouteOverride ? <RedesignLoginEntry /> : <Login />)} />
          <Route
            path="/account/recovery/:uuid/:token"
            render={(routeProps) =>
              featureFlags.redesignV1 && featureFlags.redesignV1RouteOverride ? (
                <RedesignRecoveryEntry />
              ) : (
                <PublicOnly>
                  <PasswordRecovery {...routeProps} />
                </PublicOnly>
              )
            }
          />
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/login" render={() => <RedesignLoginEntry />} /> : null}
          {featureFlags.redesignV1 ? <Route exact path="/pages/redesign/recover/password" render={() => <RedesignForgotPasswordEntry />} /> : null}
          {featureFlags.redesignV1 ? <Route path="/pages/redesign/account/recovery/:uuid/:token" render={() => <RedesignRecoveryEntry />} /> : null}
          <Route path="/payment/details" component={PaymentDetails} />
          <Route path="/contacts" component={Contacts} />
          <Route path="/patient/area/:patient_id/:token/payment/status/:invoice_id" component={PatientPaymentStatus} />
          <Route path="/patient/area/:patient_id/:token/payment/success/:hash" component={PatientPaymentSuccess} />
          <Route path="/patient/area/:patient_id/:token/payment/error" component={PatientPaymentError} />
          <Route path="/patient/area/:patient_id/:token" component={PatientArea} />
          <Route path="/pages/documents" render={() => <DocumentsRoutes />} />
          <Route path="/pages/meetings" render={() => <MeetingsRoutes />} />
          <Route path="/pages/yandex-metrika" component={requireOfferSign(YandexMetrikaPage)} />
          <Route path="/pages/events" render={() => <EventRoutes />} />
          <Route path="*" component={CatchAll} />
        </Switch>
      </DoctorConsentGate>
    </Router>
  );
}

if (!isPatientPlanPage)
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    release: `ma-frontend@${process.env.APP_VERSION}`,
    integrations: [Sentry.replayIntegration()],
    // Performance Monitoring
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    tracesSampleRate: 1.0,
    // Session Replay
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

const container = document.getElementById("react_root");
if (!container) throw new Error("Root element #react_root not found");
const root = createRoot(container);
root.render(
  <StrictMode>
    <Provider store={store}>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </Provider>
  </StrictMode>,
);

// Load dev tools after app is mounted (optional; does not block initial render)
loadDevTools(() => {});

// PWA installability
if (!isDevelopment && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((error: unknown) => {
      Sentry.captureException(error);
    });
  });

  // Notification clicks reload the target page because there is no shared router history instance.
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    if (event.data?.type === "push-navigate" && typeof event.data.url === "string") {
      window.location.assign(event.data.url);
    }
  });
}
